import React, { useState } from 'react';
import { generateReferralQR, generateUserReferralCode } from '../utils/qrReferral';
import QRCodeHistory from './QRCodeHistory';

function QRCodeGenerator() {
  const [referralCode, setReferralCode] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState('');

  const generateQRCode = async () => {
    if (!referralCode.trim()) {
      alert('Please enter a referral code');
      return;
    }

    setLoading(true);
    try {
      const result = await generateReferralQR(referralCode, {
        width: 300,
        darkColor: '#5752e8',
        lightColor: '#FFFFFF'
      });

      if (result) {
        setQrDataUrl(result.qrDataUrl);
        setGeneratedUrl(result.url);
        
        // Save to history
        if (typeof window !== 'undefined' && window.saveQRToHistory) {
          window.saveQRToHistory(result);
        }
      }
    } catch (error) {
      console.error('Error generating QR code:', error);
      alert('Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  };

  const generateUserQR = async () => {
    const userCode = generateUserReferralCode('user' + Date.now());
    setReferralCode(userCode);
    setTimeout(() => generateQRCode(), 100);
  };

  const downloadQR = () => {
    if (!qrDataUrl) return;

    const link = document.createElement('a');
    link.download = `crappermapper-qr-${referralCode}.png`;
    link.href = qrDataUrl;
    link.click();
  };

  return (
    <div className="qr-generator-container">
      <h3>QR Code Generator</h3>
      <p>Generate QR codes for referral tracking</p>
      
      <div className="qr-input-section">
        <label>Referral Code:</label>
        <input
          type="text"
          value={referralCode}
          onChange={(e) => setReferralCode(e.target.value)}
          placeholder="Enter referral code (e.g., 1234)"
        />
        <button onClick={generateUserQR} className="secondary-button">
          Generate Random Code
        </button>
      </div>

      <button 
        onClick={generateQRCode} 
        disabled={loading}
        className="primary-button"
      >
        {loading ? 'Generating...' : 'Generate QR Code'}
      </button>

      {qrDataUrl && (
        <div className="qr-result">
          <div className="qr-image-container">
            <img src={qrDataUrl} alt="QR Code" />
            <p>Referral Code: <strong>{referralCode}</strong></p>
            <p>URL: <code>{generatedUrl}</code></p>
          </div>
          
          <div className="qr-actions">
            <button onClick={downloadQR} className="success-button">
              Download QR Code
            </button>
            <button 
              onClick={() => {
                navigator.clipboard.writeText(generatedUrl);
                alert('URL copied to clipboard!');
              }}
              className="info-button"
            >
              Copy URL
            </button>
          </div>
        </div>
      )}

      <div className="qr-instructions">
        <h4>How to use:</h4>
        <ol>
          <li>Enter a referral code or generate a random one</li>
          <li>Click "Generate QR Code"</li>
          <li>Download the QR code image</li>
          <li>Print and share the QR code</li>
          <li>Track scans and conversions in PostHog dashboard</li>
        </ol>
      </div>

      <style jsx>{`
        .qr-generator-container {
          background: white;
          padding: 30px;
          border-radius: 12px;
          border: 1px solid #e9ecef;
          margin: 20px 0;
        }

        .qr-input-section {
          display: flex;
          gap: 10px;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .qr-input-section label {
          font-weight: bold;
          color: #495057;
        }

        .qr-input-section input {
          flex: 1;
          min-width: 200px;
          padding: 8px 12px;
          border: 1px solid #ced4da;
          border-radius: 4px;
          font-size: 14px;
        }

        .primary-button, .secondary-button, .success-button, .info-button {
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .primary-button {
          background: #8B4513;
          color: white;
        }

        .primary-button:hover:not(:disabled) {
          background: #6B3410;
        }

        .primary-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .secondary-button {
          background: #6c757d;
          color: white;
        }

        .secondary-button:hover {
          background: #545b62;
        }

        .success-button {
          background: #2d6657;
          color: white;
        }

        .success-button:hover {
          background: #2d6657;
        }

        .info-button {
          background: #17a2b8;
          color: white;
        }

        .info-button:hover {
          background: #138496;
        }

        .qr-result {
          margin-top: 30px;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
          text-align: center;
        }

        .qr-image-container img {
          max-width: 300px;
          height: auto;
          border: 2px solid #dee2e6;
          border-radius: 8px;
          padding: 10px;
          background: white;
        }

        .qr-image-container p {
          margin: 10px 0;
          color: #495057;
        }

        .qr-image-container code {
          background: #e9ecef;
          padding: 2px 6px;
          border-radius: 3px;
          font-family: monospace;
          font-size: 12px;
        }

        .qr-actions {
          display: flex;
          gap: 10px;
          justify-content: center;
          margin-top: 20px;
        }

        .qr-instructions {
          margin-top: 30px;
          padding: 20px;
          background: #e9ecef;
          border-radius: 8px;
        }

        .qr-instructions h4 {
          color: #495057;
          margin-bottom: 15px;
        }

        .qr-instructions ol {
          margin: 0;
          padding-left: 20px;
        }

        .qr-instructions li {
          margin-bottom: 8px;
          color: #6c757d;
        }

        @media (max-width: 768px) {
          .qr-input-section {
            flex-direction: column;
            align-items: stretch;
          }

          .qr-input-section input {
            min-width: auto;
          }

          .qr-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
}

function QRCodeGeneratorWithHistory() {
  return (
    <>
      <QRCodeGenerator />
      <QRCodeHistory />
    </>
  );
}

export default QRCodeGeneratorWithHistory;
